import AddIcon from "@mui/icons-material/AddRounded";
import type { FilledInputProps } from "@mui/material";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type {
  EntityFieldConfiguration,
  UserDefinedOptionsEntityFieldConfiguration,
} from "../../../../../api/types/objectTypes";
import type { FieldConfigurationValidation } from "../objectFieldsState";

interface Props {
  fieldConfiguration: UserDefinedOptionsEntityFieldConfiguration;
  validation: FieldConfigurationValidation;
  readOnly?: boolean;
  onChange: (fieldConfiguration: EntityFieldConfiguration) => void;
}

const UdfOptionsFieldConfigEditor = ({ fieldConfiguration, validation, readOnly, onChange }: Props) => {
  // Options carry no id and a new option's value follows its label, so rows get client-side keys.
  const [optionKeys, setOptionKeys] = useState(() => fieldConfiguration.userDefinedOptions.map(() => generateGuid()));
  const [newOptionKeys, setNewOptionKeys] = useState<string[]>([]);

  const handleAddOption = () => {
    const userDefinedOptions = fieldConfiguration.userDefinedOptions.concat({
      value: "",
      label: "",
    });

    const key = generateGuid();
    setOptionKeys((prev) => [...prev, key]);
    setNewOptionKeys((prev) => [...prev, key]);
    onChange({ ...fieldConfiguration, userDefinedOptions });
  };

  const handleEditOption =
    (index: number): FilledInputProps["onChange"] =>
    (e) => {
      const { value } = e.target;
      const userDefinedOptions = [...fieldConfiguration.userDefinedOptions];
      const currentOptionValue = userDefinedOptions[index]?.value;
      const key = optionKeys[index];
      const isNewOption = key !== undefined && newOptionKeys.includes(key);
      userDefinedOptions[index] = {
        label: value,
        value: isNewOption ? value : currentOptionValue || value,
      };

      onChange({ ...fieldConfiguration, userDefinedOptions });
    };

  const handleRemoveOption = (index: number) => () => {
    const userDefinedOptions = [...fieldConfiguration.userDefinedOptions];
    userDefinedOptions.splice(index, 1);
    const key = optionKeys[index];
    setOptionKeys((prev) => prev.filter((_, i) => i !== index));
    setNewOptionKeys((prev) => prev.filter((k) => k !== key));
    onChange({ ...fieldConfiguration, userDefinedOptions });
  };

  return (
    <Stack spacing={1} sx={{ pt: 2, overflow: "hidden" }}>
      <Typography variant="subtitle2">Options</Typography>
      <Stack spacing={1} sx={{ width: "100%", overflow: "auto", alignItems: "flex-start" }}>
        {fieldConfiguration.userDefinedOptions.map((option, index) => (
          <Stack key={optionKeys[index]} direction="row" spacing={1} sx={{ width: "100%", alignItems: "center" }}>
            <TextField
              disabled={readOnly}
              value={option.label}
              fullWidth
              onChange={handleEditOption(index)}
              error={validation.optionsResults[index]?.isValid === false}
              helperText={validation.optionsResults[index]?.error}
            />
            {!readOnly && <CloseIconButton onClick={handleRemoveOption(index)} />}
          </Stack>
        ))}
        {!readOnly && validation.result.error && <Typography color="error">{validation.result.error}</Typography>}
        {!readOnly && (
          <Button variant="text" color="secondary" onClick={handleAddOption} startIcon={<AddIcon />}>
            Add Option
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default UdfOptionsFieldConfigEditor;
